import clsx from "clsx";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";

import { RadioInput } from "@fronterahealth/frontera-ui-components";
import {
  PopoverMenu,
  PopoverMenuItem,
} from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";
import { RadioInputItem } from "@fronterahealth/frontera-ui-components/RadioInput/RadioInput";

interface InputData {
  options: string[];
  answer: string;
}

interface RadioInputFieldProps {
  inputJson: InputData;
  /**
   * Callback that receives an InputData object:
   * { options: string[], answer: string }
   */
  onChange: (json: InputData) => Promise<void>;
  actionButtonMenu: PopoverMenuItem[];
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
  };
  displayOnlyMode: boolean;
  inputName: string;
  onBlur?: () => void;
  className?: string;
}

export const RadioInputField: React.FC<RadioInputFieldProps> = ({
  inputJson,
  onChange,
  actionButtonMenu,
  permissions,
  displayOnlyMode = false,
  inputName = "",
  onBlur,
  className,
}) => {
  const convertOptions = (opts: string[]): RadioInputItem[] =>
    opts.map((o) => ({
      id: o,
      title: o,
    }));

  const [options, setOptions] = useState<RadioInputItem[]>([]);
  const [selected, setSelected] = useState<string>("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (Array.isArray(inputJson.options)) {
      setOptions(convertOptions(inputJson.options));
    } else {
      console.error("Invalid inputJson: 'options' must be an array");
      setOptions([]);
    }

    setSelected(inputJson.answer || "");
  }, [inputJson]);

  const handleRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newSelected = e.target.value;
    setSelected(newSelected);

    const output: InputData = {
      options: options.map((item) => item.title),
      answer: newSelected,
    };

    onChange(output);
  };

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (onBlur) {
          onBlur();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onBlur]);

  return (
    <div ref={containerRef} className={clsx("group relative pr-6", className)}>
      <div data-testid="radio-input-container">
        <div className="relative overflow-auto">
          <RadioInput
            items={selected ? [...options, { id: "", title: "--" }] : options}
            title=""
            name={inputName}
            legend="Report"
            onChange={handleRadioChange}
            displayOnlyMode={displayOnlyMode}
            selectedId={selected}
            variant={displayOnlyMode ? "default" : "report"}
            onBlur={onBlur}
            inputContainerClassName={!displayOnlyMode ? "min-h-[48px]! rounded-lg mb-[10px]" : ""}
          />
        </div>
      </div>
      <div
        data-testid="radio-actions-panel"
        className={`group absolute -right-1 top-1 flex-col gap-y-4 ${
          permissions.canEdit ? "opacity-0 group-hover:opacity-100" : "pointer-events-none opacity-0"
        } mt-2 transition-all`}
      >
        {actionButtonMenu.length ? (
          <PopoverMenu mode="secondary" menuItems={actionButtonMenu} openDirection="to-bottom-left" />
        ) : null}
      </div>
    </div>
  );
};
